import { useEffect } from 'react';

import { navigate, redirectTo, useLocation } from '@xing-com/crate-core-router';
import { XING_JOBS_SEARCH_KI_PATH } from '@xing-com/crate-core-routes';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import { useSearchParameters } from '@xing-com/crate-hooks-use-search-parameters';
import { SearchAlertsEmailBanner } from '@xing-com/crate-jobs-components-search-alerts-email-banner';
import {
  SEARCH_ALERTS_MODAL_PARAM,
  SEARCH_ALERTS_MODAL_TYPES,
  SEARCH_QUERY_ID_PARAM,
  SEARCH_STATE_PARAMS,
  SMALL_SCREEN_BOTTOM_SHEET_PARAM,
} from '@xing-com/crate-jobs-constants';
import { SearchMetadata } from '@xing-com/crate-jobs-domain-search-components-metadata';
import { SerpsMetadata } from '@xing-com/crate-jobs-domain-serps-components-metadata';
import type { SerpsRouteParams } from '@xing-com/crate-jobs-domain-serps-helpers';
import { getJobsSerpsSearchParameters } from '@xing-com/crate-jobs-domain-serps-helpers';
import { useSerpsMatch } from '@xing-com/crate-jobs-domain-serps-hooks';
import {
  useCurrentSearchAlertStatus,
  useMarkSearchAlertAsVisited,
  useSearchState,
} from '@xing-com/crate-jobs-hooks';
import { useSearchTracking } from '@xing-com/crate-jobs-tracking';
import {
  SearchAlertsBanner,
  SearchAlertsErrorBanner,
  SearchAlertsSearchModal,
} from '@xing-com/crate-jobs-xinglets';

import {
  getPaginationResultCount,
  getSearchResultsPageSize,
  getSerpPageUrl,
  getTotalPages,
  isClassicSerpPaginationEnabled,
  increaseSearchCount,
  isVisibleJob,
  normalizeBoundedTotal,
} from '../../helpers';
import { useSearch } from '../../hooks';
import { AppliedFilters } from '../shared';

import { EmptyState } from './empty-state';
import { ErrorState } from './error-state';
import { transformResultsList } from './helpers';
import { LazyLoader } from './lazy-loader';
import { SerpNearbyResults } from './nearby-results/serp-nearby-results';
import { NotEnoughProfileDataBanner } from './not-enough-profile-data-banner';
import { Pagination } from './pagination';
import { ResultsHeader } from './results-header';
import { ResultsList } from './results-list';
import { ResultsSkeleton } from './results-skeleton';
import * as S from './results.styles';

const REPETITION_TRIGGERS = [3, 9, 16];

type LayoutWithMetadata = {
  children: React.ReactNode;
  serps: SerpsRouteParams | null;
  jobCount?: number;
  page?: number;
};

const ResultsLayoutWithMetadata: React.FC<LayoutWithMetadata> = ({
  children,
  serps,
  jobCount,
  page,
}) => (
  <div>
    {serps ? (
      <SerpsMetadata {...serps} jobCount={jobCount} page={page} />
    ) : (
      <SearchMetadata />
    )}
    {children}
  </div>
);

type Props = {
  isStickyHeader?: boolean;
};

export const Results: React.FC<Props> = ({ isStickyHeader }) => {
  const { isLoggedOut } = useLoginState();
  const { pathname, search } = useLocation();
  const serps = useSerpsMatch();
  const { trackPageview } = useSearchTracking();
  const { getSearchParameter } = useSearchParameter();
  const { setSearchParameters } = useSearchParameters();
  const {
    searchAlertId,
    isLoading: isSearchAlertsLoading,
    isSearchIdAnAlert,
  } = useCurrentSearchAlertStatus();
  const { filterCount, page, sort, since, sincePeriod } = useSearchState();
  const { isLoading, results, refetch, fetchMore } = useSearch();
  const isClassicPagination = isClassicSerpPaginationEnabled({
    serps,
    isLoggedOut,
  });
  const pageSize = getSearchResultsPageSize({
    serps,
    isLoggedOut,
  });
  const metadataPage = isClassicPagination ? page : 1;

  useMarkSearchAlertAsVisited(searchAlertId);

  const isFilterView = !!getSearchParameter(SMALL_SCREEN_BOTTOM_SHEET_PARAM);
  const newSearchId = results?.searchQuery.guid;

  useEffect(() => {
    if (isSearchAlertsLoading) {
      return;
    }

    if (newSearchId && !isFilterView) {
      trackPageview({
        results,
        sort,
        page,
        filterCount,
        shouldTrackNwt: true,
      });
    }

    if (serps) {
      const queryParameters = {
        ...getJobsSerpsSearchParameters(serps),
        [SEARCH_STATE_PARAMS.page]:
          isClassicPagination && page > 1 ? page.toString() : '',
      };

      if (!isLoggedOut) {
        const searchParameters = new URLSearchParams(
          Object.entries(queryParameters).filter(([, value]) => Boolean(value))
        );

        navigate(`${XING_JOBS_SEARCH_KI_PATH}?${searchParameters.toString()}`, {
          replace: true,
        });
      } else {
        setSearchParameters(queryParameters, {
          removeEmpty: true,
          replace: true,
        });
      }
      return;
    }

    if (!newSearchId) {
      return;
    }

    const keywords = results.searchQuery.body?.keywords;
    const location = results.searchQuery.body?.location;
    const canSearchBeSaved = !!(keywords || location);
    const repetitionCount = increaseSearchCount(newSearchId);
    const isActiveSA = isSearchIdAnAlert(newSearchId);

    const shouldShowRepeatedModal =
      canSearchBeSaved &&
      !isActiveSA &&
      REPETITION_TRIGGERS.includes(repetitionCount) &&
      !isFilterView;

    const queryParameters = {
      ...(shouldShowRepeatedModal
        ? { [SEARCH_ALERTS_MODAL_PARAM]: SEARCH_ALERTS_MODAL_TYPES.REPEAT }
        : {}),
      // We add the search alert query to the URL, so other components (i.e. search alerts)
      // can access it without the need of using the useSearch hook
      [SEARCH_QUERY_ID_PARAM]: newSearchId,
    };

    setSearchParameters(queryParameters, { replace: true });
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [newSearchId, isSearchAlertsLoading, sort, sincePeriod]);

  if (isLoading) {
    return (
      <ResultsLayoutWithMetadata serps={serps} page={metadataPage}>
        <ResultsSkeleton />
      </ResultsLayoutWithMetadata>
    );
  }
  if (!results?.collection) {
    return (
      <ResultsLayoutWithMetadata serps={serps} page={metadataPage}>
        <ErrorState refetch={refetch} />
      </ResultsLayoutWithMetadata>
    );
  }

  const filteredCollection = results.collection.filter(isVisibleJob);
  const totalPages = getTotalPages({
    boundedTotal: results.boundedTotal,
    total: results.total,
    pageSize,
  });
  const lastPage = Math.max(1, totalPages);

  if (isClassicPagination && page > lastPage) {
    redirectTo(getSerpPageUrl({ pathname, page: lastPage, search }), 301);
    return null;
  }

  const normalizedBoundedTotal = normalizeBoundedTotal(results.boundedTotal);
  const jobCount = getPaginationResultCount({
    boundedTotal: results.boundedTotal,
    total: results.total,
  });
  const metadataJobCount = normalizedBoundedTotal?.displayTotal ?? jobCount;

  if (filteredCollection.length === 0) {
    return (
      <ResultsLayoutWithMetadata
        serps={serps}
        jobCount={metadataJobCount}
        page={metadataPage}
      >
        <NotEnoughProfileDataBanner />
        <SearchAlertsEmailBanner />
        <EmptyState serps={serps} hasSinceFilter={!!since} />
        <SerpNearbyResults
          serps={serps}
          mainResults={filteredCollection}
          primaryJobCount={jobCount}
        />
      </ResultsLayoutWithMetadata>
    );
  }

  const { items, urgentJobsHighlighted } = transformResultsList(
    filteredCollection,
    { duplicateUrgentJobs: !isClassicPagination }
  );

  return (
    <ResultsLayoutWithMetadata
      serps={serps}
      jobCount={metadataJobCount}
      page={metadataPage}
    >
      <NotEnoughProfileDataBanner />
      <SearchAlertsEmailBanner />
      <ResultsHeader
        jobCount={jobCount}
        serps={serps}
        boundedTotals={results.boundedTotal}
        isStickyHeader={isStickyHeader}
      />
      <S.MediaConfined>
        <AppliedFilters />
      </S.MediaConfined>
      <ResultsList
        items={items}
        type="SEARCH"
        urgentJobsHighlighted={urgentJobsHighlighted}
      />
      <SerpNearbyResults
        serps={serps}
        mainResults={filteredCollection}
        primaryJobCount={jobCount}
      />
      {isClassicPagination ? (
        <Pagination
          currentPage={page}
          pathname={pathname}
          search={search}
          totalPages={totalPages}
        />
      ) : (
        <LazyLoader
          isSerps={Boolean(serps && isLoggedOut)}
          hasSinceFilter={!!since}
          fetchMore={fetchMore}
          results={results}
        />
      )}
      <SearchAlertsSearchModal />
      <SearchAlertsBanner />
      <SearchAlertsErrorBanner />
    </ResultsLayoutWithMetadata>
  );
};
